import { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { getOrders, type OrderSummary } from '@/api/orders';
import { StatusPill } from '@/components/Status';
import { statusInk } from '@/lib/status';
import { STATUS_LABELS, fmtDate } from './lib';
import { WorkNotice } from './workLib';

/**
 * "Pick a journey" — a search-and-rank list of open journeys, where the caller
 * decides what picking one MEANS. Follow-ups uses it to attach an unattributed
 * parcel, which writes a tracking_no ref onto the chosen journey.
 *
 * This is the generic half of sundae's AddToOrderJourneyDialog. That dialog
 * stays in sundae because its default action links a MAIL MESSAGE to a journey
 * — a mail-reader affordance this app has no business owning (plan §8b). Only
 * the picker came across, with `onPick` required rather than defaulted.
 */
export function PickJourneyDialog({
  open,
  onOpenChange,
  vendorGuess,
  title = 'Pick a journey',
  description,
  successNote = 'Done.',
  onPick,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Vendor name to float to the top — seeds the ranking, never a filter. */
  vendorGuess?: string | null;
  title?: string;
  description?: string;
  successNote?: string;
  onPick: (order: OrderSummary) => Promise<void>;
}) {
  const [orders, setOrders] = useState<OrderSummary[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [linkedTo, setLinkedTo] = useState<OrderSummary | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setOrders(null);
    setLoadError(null);
    setSearch('');
    setLinkedTo(null);
    setLinkError(null);
    getOrders()
      .then(setOrders)
      .catch((e: Error) => setLoadError(e.message));
  }, [open]);

  const rows = useMemo(() => {
    if (!orders) return [];
    const q = search.trim().toLowerCase();
    const guess = (vendorGuess || '').toLowerCase();
    let out = orders.filter((o) => !o.archivedAt);
    if (q) {
      out = out.filter(
        (o) =>
          o.vendor.toLowerCase().includes(q) ||
          (o.label || '').toLowerCase().includes(q) ||
          o.receivingIds.some((id) => String(id).includes(q))
      );
    }
    // Vendor-matched journeys float; within a band, most recently updated first.
    const score = (o: OrderSummary) => {
      const v = o.vendor.toLowerCase();
      if (!guess) return 0;
      if (guess.includes(v) || v.includes(guess)) return 1;
      return v.split(/\s+/).some((word) => word.length > 3 && guess.includes(word)) ? 0.5 : 0;
    };
    return [...out].sort(
      (a, b) => score(b) - score(a) || (b.updatedAt || '').localeCompare(a.updatedAt || '')
    );
  }, [orders, search, vendorGuess]);

  const pick = async (order: OrderSummary) => {
    setBusyId(order.id);
    setLinkError(null);
    try {
      await onPick(order);
      setLinkedTo(order);
    } catch (e) {
      setLinkError(e instanceof Error ? e.message : 'Failed to link to the journey');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        {linkedTo ? (
          <WorkNotice tone="success">
            Linked to {linkedTo.vendor}
            {linkedTo.label ? ` — ${linkedTo.label}` : ''}. {successNote}
          </WorkNotice>
        ) : (
          <>
            <Input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search vendor, note, or receiving #"
              className="h-8 text-sm"
            />
            {linkError && <div className={cn('text-xs', statusInk('danger'))}>{linkError}</div>}
            <div className="h-[45vh] overflow-y-auto rounded-md border border-border/60">
              {loadError && <div className={cn('p-3 text-xs', statusInk('danger'))}>{loadError}</div>}
              {!orders && !loadError && (
                <div className="flex items-center justify-center gap-2 py-8 text-xs text-muted-foreground">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading journeys…
                </div>
              )}
              {orders && rows.length === 0 && (
                <div className="py-8 text-center text-xs text-muted-foreground">
                  No matching journeys.
                </div>
              )}
              {rows.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  disabled={busyId != null}
                  className="flex w-full flex-wrap items-center gap-x-2 gap-y-0.5 border-b border-border/40 px-3 py-2 text-left text-sm hover:bg-muted/40 disabled:opacity-60"
                  onClick={() => pick(o)}
                >
                  {busyId === o.id ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                  ) : null}
                  <span className="font-medium">{o.vendor}</span>
                  {o.label && (
                    <span className="truncate text-xs text-muted-foreground">{o.label}</span>
                  )}
                  <StatusPill className={cn((o.status === 'cancelled' || o.status === 'wont_order') && 'line-through')}>
                    {STATUS_LABELS[o.status] || o.status}
                  </StatusPill>
                  <span className="ml-auto text-xs tabular-nums text-muted-foreground">
                    {o.placedDate
                      ? `placed ${fmtDate(o.placedDate)}`
                      : o.orderDueDate
                        ? `due ${fmtDate(o.orderDueDate)}`
                        : ''}
                  </span>
                </button>
              ))}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
